import { gEnums } from "global/enums/globalEnums";
import { useContext, useEffect, useMemo, useState } from "react";
import { _matchDisplayTypes } from "../elements/matches/MatchContent";
import { SportsSeasonContext } from "../cnr/contexts/SportsSeasonContext";
import { matchFilterHelpers } from "../elements/matches/MatchFilters";
import { ParentContext } from "global/cnr/contexts/ParentContext";
import { MatchDataUpdateContext } from "../elements/matches/MatchDataUpdate";
import { SeasonMatchesContext } from "../cnr/contexts/SeasonMatchesContext";
import { getLevelMatchez } from "../cnr/reducers/reducerHelpers/seasonSportsData";
import _ from "lodash";
import { _matchLsTypes } from "../cnr/reducers/SportsDataReducer";

export const useMatchSchedule = (props, selectedDate) => {

  const msdts = gEnums.matchScheduleDisplayTypes

  const {
    _levelDateGroups,
    directMatches,
    forBackup,
    forPlayoff,
    matchScheduleDisplayType,
    matchTeams,
    teamKey,
  } = props;

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {};
  const { paps_state } = states ?? {};

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};
  const { matches_info, seasonMatches_info } = sportsSeason_state ?? {};
  const _matches_info = forBackup && seasonMatches_info ? seasonMatches_info : matches_info;
  const { matches_groupInfo, matches_matchDay } = _matches_info ?? {};
  const { levelDateGroups, matches_combined } = matches_groupInfo ?? {};

  const dateToTimestamp = (dateStr) => {
    const [month, day, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  // seasonMatchesContext
  const seasonMatchesContext = useContext(SeasonMatchesContext)
  const { seasonMatches_state } = seasonMatchesContext ?? {}
  const { matchFilter } = seasonMatches_state ?? {}

  // matchDataUpdateContext
  const matchDataUpdateContext = useContext(MatchDataUpdateContext);
  const { sportsData_state } = matchDataUpdateContext ?? {};
  const { matchPlayoffPendings } = sportsData_state ?? {};

  const { pathViews } = paps_state ?? {};

  // gets the summary of matches
  const matchesSummary = useMemo(() => {
    const { matches, matches_playoffs } = _matches_info ?? {}
    const _matches = _.values(matches ?? {});
    const _playoffMatches = _.values(matches_playoffs ?? {});
    const matches_combined = [..._matches, ..._playoffMatches];
    const total = _.size(matches_combined);
    const completed = _.filter(matches_combined, (m) => !!m?.results).length;
    const matchDay = _.filter(matches_combined, (m) => !!m?._results_matchDay).length;
    const missing = _.filter(matches_combined, (m) => !!m?._status?.isMissing).length;
    return { total, completed, missing, matchDay };
  }, []);


  const { levelSectionDateMatches, scheduleLevelKeys } = useMemo(() => {

    // Step 1: group by _matchCollectionKey (or 'levels')
    const groupedByLevel = _.groupBy(_matches_info?.matches, '_matchCollectionKey');

    // Step 2: for each level, group by startDate and sort chronologically
    const matches_byLevel = _.mapValues(groupedByLevel, (levelGroup) => {

      // group by startDate
      const groupedByDate = _.groupBy(levelGroup, 'startDate');

      // sort the dates
      const sortedByDate = {};
      _.keys(groupedByDate) // using _.keys here
        .sort((a, b) => dateToTimestamp(a) - dateToTimestamp(b))
        .forEach((date) => {
          // group by sections within each date
          const matches = _.groupBy(groupedByDate[date], 'sections');
          sortedByDate[date] = matches
        });

      return sortedByDate;
    });


    const matches_added = []

    _.forEach(matches_byLevel, (matches_level, levelKey) => {
      switch (levelKey) {
        case 'Non':
        case 'nonDistrict':
          _.forEach(matches_level, (groupedMatches, groupDateKey) => {
            _.forEach(groupedMatches, (dateMatches, dataMatchKey) => {
              _.forEach(dateMatches, (match) => {
                const { home, away, _itemKey: matchKey } = match;
                const level_home = home?.levels;
                const level_away = away?.levels;

                if (level_home && !_.includes(matches_added, matchKey)) {
                  _.update(
                    matches_byLevel,
                    [level_home, groupDateKey, dataMatchKey],
                    (arr = []) => {
                      arr.push(match);
                      return arr;
                    }
                  );
                  matches_added.push(matchKey)
                }

                if (level_away && !_.includes(matches_added, matchKey)) {
                  _.update(
                    matches_byLevel,
                    [level_away, groupDateKey, dataMatchKey],
                    (arr = []) => {
                      arr.push(match);
                      return arr;
                    }
                  );
                  matches_added.push(matchKey)
                }
              });
            });
          });
      }
    });
    return { levelSectionDateMatches: matches_byLevel, scheduleLevelKeys: _.keys(matches_byLevel) }
    // return matches_byLevel
  }, [_matches_info?.matches])

  const [matchScheduleInfo, setMatchScheduleInfo] = useState({})

  let _matchScheduleDisplayType = matchScheduleDisplayType ? matchScheduleDisplayType : msdts.clickedDates

  const matchLevelGroups = useMemo(() => {

    if (!matches_combined) return

    switch (_matchScheduleDisplayType) {
      case msdts.direct:
        return directMatches

      case msdts.currentMatches:
        return _levelDateGroups

      case msdts.allDates:
      case msdts.clickedDates:
      case msdts.levelDates:
      case msdts.levelSelectedDate:
      case msdts.levelTabsDates:
      case msdts.levelTabsFull:
      case msdts.selectedDate:
        // console.log('levelSectionDateMatches', levelSectionDateMatches)
        // console.log('levelDateGroups', levelDateGroups)
        return levelDateGroups

      case msdts.scoreboard:
        return matchFilterHelpers.getTodaysLevelMatches(levelDateGroups, 8, selectedDate)

      case msdts.latestMatches:
        return matchFilterHelpers.getLatestMatches(matches_combined, teamKey ? teamKey : pathViews.teams, 4)

      case msdts.nextTeamMatch:
        const nm = matchFilterHelpers.getNextMatch(matches_combined, teamKey ? teamKey : pathViews.teams)
        return nm ? [nm] : []

      case msdts.teamSchedule:
        if (!_.isEmpty(matchFilter)) {
          return matchFilterHelpers.getFilterMatches(matches_combined, teamKey ? teamKey : pathViews.teams, matchFilter)
        }
        return matchFilterHelpers.getMatchesForTeam(matches_combined, teamKey ? teamKey : pathViews.teams)

      case msdts.teamScheduleRemaining:
        return matchFilterHelpers.getMatchesForTeam(matches_combined, teamKey ? teamKey : pathViews.teams, forPlayoff ? false : true)

      case msdts.missing:
        return matchFilterHelpers.getMissingMatches(matches_combined)

      case msdts.matchDay:
        return matches_matchDay && matchFilterHelpers.getMatchDayMatches(matches_combined, matches_matchDay)

      case msdts.pendingMatches:
        if (matchPlayoffPendings) {
          const { _levelDateGroups } = getLevelMatchez(matchPlayoffPendings);
          return _levelDateGroups
        }
        const x = _matches_info && matchFilterHelpers.getPendingMatches(_matches_info, true)
        return x

      case msdts.teamsCommon:
        const tdms = _matches_info && matchFilterHelpers.getTeamDirectMatches(matches_combined, matchTeams)
        return tdms

    }

    return {}

  }, [levelSectionDateMatches, scheduleLevelKeys, matches_combined, _matches_info, _matchScheduleDisplayType, matchFilter, _levelDateGroups, matches_matchDay, selectedDate]);

  useEffect(() => {
    const _matchScheduleInfo = {
      matchLevelGroups,
      matchLevelReversedGroups: _.reverse(_.toPairs(matchLevelGroups)),
      levelSectionDateMatches,
      scheduleLevelKeys,
      matchesSummary
    }
    setMatchScheduleInfo(_matchScheduleInfo)
  }, [_matchScheduleDisplayType, matchLevelGroups]);

  return matchScheduleInfo

};